import { useState } from 'react';
import { useForm } from 'react-hook-form';
import Image from 'next/image';
import ErrorMessage from '@/components/common/ErrorMessage';
import Layout from '@/components/layout';
import { Pages } from '@/lib/seoData';
import { withServerSideProps, WithGetServerSidePropsContext, NextSSRPage } from 'hoc/withServerSideProps';

type ContactFormField = {
  name: string;
  email: string;
  message: string;
};

const ContactPage: NextSSRPage = (props) => {
  const [error, setError] = useState<boolean>(false);
  const {
    register,
    reset,
    handleSubmit,
    formState: { errors }
  } = useForm<ContactFormField>();
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const onSubmit = async (data: ContactFormField) => {
    setError(false);
    setIsSubmitted(false);
    fetch('/api/contact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    }).then((res) => {
      if (res.status === 200) {
        setIsSubmitted(true);
        reset();
      } else {
        setError(true);
      }
    });
  };

  return (
    <Layout {...props}>
      <div className="flex flex-col justify-center items-center pt-20 pb-10 px-4 lg:px-16">
        <h1 className="leading-[3.5rem] mb-10">Send us a message</h1>
        <p className="text-center max-w-3xl">
          Need help? If our FAQs page hasn&apos;t already answered your question
          please send us a message and our customer care team respond back as
          soon as possible. If your query relates to a specific order or
          product, please include your order number or product details. Thank
          you.
        </p>
      </div>
      <div className="flex flex-col justify-center items-center pb-16 px-4 lg:px-16">
        <div className="flex flex-col max-w-3xl w-full lg:px-16">
          <form onSubmit={handleSubmit(onSubmit)} className="my-9 flex-1">
            <label className="relative">
              <p className="">Name*</p>
              <input
                className={`border pl-3 py-3 w-full focus:outline-none focus:border-onyx mt-2.5`}
                type="text"
                {...register('name', {
                  required: 'Name is required'
                })}
              />
            </label>
            <label className="relative">
              <p className="mt-7">Email*</p>
              <input
                className={`border pl-3 py-3 w-full focus:outline-none focus:border-onyx mt-2.5`}
                type="text"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,5}$/i,
                    message: 'This field must contain a valid email.'
                  }
                })}
              />
            </label>
            {errors.email && (
              <div className="text-sm flex items-center justify-between my-4 text-[#E47869]">
                <span className="mr-4">
                  <ErrorMessage message={errors.email.message as string} />
                </span>
                <Image
                  src="/exclamation.svg"
                  alt="Correct"
                  width={15}
                  height={15}
                />
              </div>
            )}
            <label className="relative">
              <p className="mt-7">Message*</p>
              <textarea
                className={`border pl-3 py-3 w-full focus:outline-none focus:border-onyx mt-2.5`}
                {...register('message', {
                  required: 'Message is required'
                })}
              />
            </label>
            {error && (
              <div className="text-sm flex items-center justify-between my-4 text-[#E47869]">
                <span className="mr-4">
                  <ErrorMessage
                    message={'Something went wrong. Please try again later.'}
                  />
                </span>
                <Image
                  src="/exclamation.svg"
                  alt="Correct"
                  width={15}
                  height={15}
                />
              </div>
            )}
            {isSubmitted && (
              <div className="flex flex-col items-center">
                <p className="text-center mt-10">
                  Thanks for contacting us! We will get back to you as soon as
                  possible.
                </p>
              </div>
            )}
            <div className="px-8 flex flex-row items-center justify-center">
              <button
                className="bg-onyx border py-4 px-2 text-white mt-10 uppercase w-3/4"
                type="submit"
              >
                Send
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export const getServerSideProps = withServerSideProps(async function (context: WithGetServerSidePropsContext) {
  const { accessToken } = context.props;
  if (accessToken) {
    return {
      redirect: {
        destination: '/',
        permanent: false
      }
    };
  }
  return {
    props: {}
  };
}, Pages.CONTACT)

export default ContactPage;
